import React, { useState } from 'react';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addDays,
  nextMonday,
  parse,
  isValid,
} from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu';

interface CalendarPickerDropdownProps {
  value?: string;
  onChange: (formattedDate: string) => void;
  trigger?: React.ReactNode;
  align?: 'start' | 'end' | 'center';
  placeholder?: string;
}

export function CalendarPickerDropdown({
  value,
  onChange,
  trigger,
  align = 'end',
  placeholder = 'Not scheduled',
}: CalendarPickerDropdownProps) {
  const [open, setOpen] = useState(false);

  // Try parsing the current value if possible, fallback to today
  const parseCurrentDate = (val?: string): Date => {
    if (!val || val === 'Not scheduled' || val === 'Tomorrow') return new Date();
    try {
      // Try parsing standard formats like 'MMM d, yyyy' or 'yyyy-MM-dd'
      const parsedStandard = parse(val, 'MMM d, yyyy', new Date());
      if (isValid(parsedStandard)) return parsedStandard;
      const parsedIso = parse(val, 'yyyy-MM-dd', new Date());
      if (isValid(parsedIso)) return parsedIso;
      const nativeParsed = new Date(val);
      if (isValid(nativeParsed)) return nativeParsed;
    } catch {
      // ignore
    }
    return new Date();
  };

  const [currentMonth, setCurrentMonth] = useState<Date>(() => parseCurrentDate(value));
  const selectedDate = value && value !== 'Not scheduled' ? parseCurrentDate(value) : null;

  const handlePrevMonth = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMonth((prev) => subMonths(prev, 1));
  };

  const handleNextMonth = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMonth((prev) => addMonths(prev, 1));
  };

  const handleSelectDay = (day: Date) => {
    const formatted = format(day, 'MMM d, yyyy');
    onChange(formatted);
    setOpen(false);
  };

  const handleQuickPreset = (preset: 'today' | 'tomorrow' | 'nextWeek' | 'clear') => {
    if (preset === 'clear') {
      onChange(placeholder);
    } else if (preset === 'today') {
      onChange(format(new Date(), 'MMM d, yyyy'));
    } else if (preset === 'tomorrow') {
      onChange(format(addDays(new Date(), 1), 'MMM d, yyyy'));
    } else if (preset === 'nextWeek') {
      onChange(format(nextMonday(new Date()), 'MMM d, yyyy'));
    }
    setOpen(false);
  };

  // Calendar days grid calculation
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: startDate, end: endDate });

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {trigger ? (
          trigger
        ) : (
          <button
            type="button"
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            title="Open Calendar"
          >
            <CalendarIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={align}
        className="w-72 p-3 bg-popover border border-border shadow-2xl rounded-xl space-y-3 z-50 text-foreground"
      >
        {/* Quick Presets Header */}
        <div className="flex items-center gap-1.5 pb-2 border-b border-border/60">
          <button
            type="button"
            onClick={() => handleQuickPreset('today')}
            className="px-2 py-1 text-[11px] font-medium rounded-md bg-muted/60 hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => handleQuickPreset('tomorrow')}
            className="px-2 py-1 text-[11px] font-medium rounded-md bg-muted/60 hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer"
          >
            Tomorrow
          </button>
          <button
            type="button"
            onClick={() => handleQuickPreset('nextWeek')}
            className="px-2 py-1 text-[11px] font-medium rounded-md bg-muted/60 hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer"
          >
            Next Mon
          </button>
          {value && value !== placeholder && (
            <button
              type="button"
              onClick={() => handleQuickPreset('clear')}
              className="ml-auto p-1 text-[11px] rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
              title="Clear date"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between px-1">
          <span className="text-xs font-bold text-foreground">
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              title="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              title="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Days of Week */}
        <div className="grid grid-cols-7 gap-1 text-center">
          {weekDays.map((wd) => (
            <span key={wd} className="text-[10px] font-semibold text-muted-foreground uppercase">
              {wd}
            </span>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-1 text-center">
          {days.map((day) => {
            const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isCurrentDay = isToday(day);

            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => handleSelectDay(day)}
                className={`h-7 w-7 mx-auto flex items-center justify-center text-xs rounded-md transition-all cursor-pointer font-medium ${
                  isSelected
                    ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                    : isCurrentDay
                    ? 'border border-primary text-primary font-bold hover:bg-muted'
                    : !isCurrentMonth
                    ? 'text-muted-foreground/35 hover:bg-muted/40'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>

        {/* Current status footer */}
        <div className="pt-2 border-t border-border/60 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>Selected:</span>
          <span className="font-semibold text-foreground">
            {value && value !== placeholder ? value : 'None'}
          </span>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
